import { QueryHookOptions } from '@apollo/react-hooks';
import dayjs, { Dayjs } from 'dayjs';
import Config from 'react-native-config';
import { useDispatch, useSelector } from 'react-redux';
import { useProxySwitcherEffect } from 'hooks/useProxySwitcherEffect';
import { useQuery } from 'hooks/useQuery';
import { GetMedicalTestResultList } from 'services/orchestration/GetMedicalTestResultList';
import { OperationVariables } from 'apollo-client';
import { getTestResultsList } from 'store/testResults/actions';
import { INIT_YEARS_TO_QUERY } from 'store/testResults/constants';
import {
  testResultsListDataSelector,
  testResultsListIsFetchingSelector,
  testResultsListErrorSelector
} from 'store/testResults/selectors';

interface GetMedicalTestResultListProps {
  startDate?: Date | Dayjs;
  endDate?: Date | Dayjs;
  customGqlOptions?: OperationVariables;
  skipAll?: boolean; // if we already have the data and don't need to retrieve it here
}

export const formatDateForQuery = (date: Date | Dayjs | string) => {
  return dayjs(date).format('YYYY-MM-DD');
};

export const getDefaultStartDate = () => dayjs().subtract(INIT_YEARS_TO_QUERY, 'year');
export const getDefaultEndDate = () => dayjs();

const queryOptions: QueryHookOptions = {
  notifyOnNetworkStatusChange: true,
  context: {
    clientName: 'orchestration',
    useFhir: true
  },
  fetchPolicy: 'cache-first'
};

export const useGetMedicalTestResultList = (props?: GetMedicalTestResultListProps) => {
  const {
    startDate = getDefaultStartDate(),
    endDate = getDefaultEndDate(),
    customGqlOptions = {},
    skipAll = false
  } = props || {};
  const fStartDate = formatDateForQuery(startDate);
  const fEndDate = formatDateForQuery(endDate);
  const restData = useSelector(testResultsListDataSelector);
  const restIsFetching = useSelector(testResultsListIsFetchingSelector);
  const restError = useSelector(testResultsListErrorSelector);
  const dispatch = useDispatch();
  const useQueryResult = useQuery(GetMedicalTestResultList, {
    variables: {
      startDate: fStartDate,
      endDate: fEndDate
    },
    ...queryOptions,
    ...customGqlOptions,
    skip: customGqlOptions?.skip || skipAll || !!Config.TEST_RESULTS_EXPANSION_ENABLED
  });

  useProxySwitcherEffect(() => {
    if (!!Config.TEST_RESULTS_EXPANSION_ENABLED && !skipAll) {
      dispatch(getTestResultsList(fStartDate, fEndDate, false));
    }
  }, [skipAll]);

  if (!!Config.TEST_RESULTS_EXPANSION_ENABLED) {
    return {
      data: restData,
      loading: restIsFetching,
      error: restError,
      refetch: () => dispatch(getTestResultsList(fStartDate, fEndDate, false))
    };
  }

  return {
    ...useQueryResult,
    data: useQueryResult?.data?.getMedicalTestResultList?.results || []
  };
};
